"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import type { FirstOffer, RecoveryOffer } from "@/stores/slices/userSlice";

export const VIP_RENEWAL_INCENTIVE_BACKGROUND =
  "https://oss.findinai.cn/storage/20260615/07f6998b3f55bfc192953cc445717c6a.png";

export type VipIncentiveOfferKind = "first" | "recovery";

export type VipIncentiveOffer = {
  active: false;
} | {
  active: true;
  kind: VipIncentiveOfferKind;
  remainingSeconds: number;
  amount: number;
  endTime: number;
};

export type ActiveVipIncentiveOffer = Extract<VipIncentiveOffer, { active: true }>;

export const inactiveVipIncentiveOffer: VipIncentiveOffer = { active: false };

export function getFirstVipIncentiveOffer(firstOffer: FirstOffer): VipIncentiveOffer {
  if (!firstOffer.active) {
    return inactiveVipIncentiveOffer;
  }

  return {
    active: true,
    kind: "first",
    remainingSeconds: firstOffer.remainingSeconds,
    amount: firstOffer.bonusBeans,
    endTime: firstOffer.endTime,
  };
}

export function getRecoveryVipIncentiveOffer(recoveryOffer: RecoveryOffer): VipIncentiveOffer {
  if (!recoveryOffer.active) {
    return inactiveVipIncentiveOffer;
  }

  return {
    active: true,
    kind: "recovery",
    remainingSeconds: recoveryOffer.remainingSeconds,
    amount: recoveryOffer.recoverableAmount,
    endTime: recoveryOffer.endTime,
  };
}

export function getActiveVipIncentiveOffer({
  firstOffer,
  recoveryOffer,
}: {
  firstOffer: FirstOffer;
  recoveryOffer: RecoveryOffer;
}): VipIncentiveOffer {
  if (firstOffer.active) {
    return getFirstVipIncentiveOffer(firstOffer);
  }

  if (recoveryOffer.active) {
    return getRecoveryVipIncentiveOffer(recoveryOffer);
  }

  return inactiveVipIncentiveOffer;
}

export function getInitialRemainingSeconds(offer: VipIncentiveOffer) {
  if (!offer.active) {
    return 0;
  }

  const endTimeRemainingSeconds = Math.max(0, Math.floor(offer.endTime - Date.now() / 1000));
  if (Number.isFinite(offer.remainingSeconds) && offer.remainingSeconds > 0) {
    return Math.min(Math.floor(offer.remainingSeconds), endTimeRemainingSeconds);
  }

  return endTimeRemainingSeconds;
}

export function splitCountdown(totalSeconds: number) {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return {
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
}

export function formatOfferBeans(amount: number) {
  return new Intl.NumberFormat("zh-CN", {
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(amount / 100);
}

export function getVipIncentiveCopy({
  offer,
  offerBeans,
}: {
  offer: VipIncentiveOffer;
  offerBeans: string;
}) {
  if (offer.active && offer.kind === "recovery") {
    return {
      badge: "限时续费礼",
      title: `续费 Plus 会员返还 ${offerBeans} 飞豆`,
      subtitle: "开启更强专业 AI 能力，享受更多优惠",
      footerText: `限时：续费会员返还 ${offerBeans} 飞豆`,
      modalCtaLabel: "立即升级",
      footerCtaLabel: "立即续费",
    };
  }

  return {
    badge: "限时会员礼",
    title: `升级 Plus 会员加赠 ${offerBeans} 飞豆`,
    subtitle: "开启更强专业 AI 能力，享受更多优惠",
    footerText: `限时：升级会员加赠 ${offerBeans} 飞豆`,
    modalCtaLabel: "立即升级",
    footerCtaLabel: "立即升级",
  };
}

export function useVipRenewalIncentiveCountdown(
  offer: VipIncentiveOffer,
  onExpire?: (offer: ActiveVipIncentiveOffer) => void,
) {
  const [countdownEndAt, setCountdownEndAt] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const expiredOfferKeyRef = useRef("");
  const onExpireRef = useRef(onExpire);

  const initialRemainingSeconds = useMemo(
    () => getInitialRemainingSeconds(offer),
    [offer],
  );
  const offerEndTime = offer.active ? offer.endTime : 0;
  const activeOfferKey = offer.active ? `${offer.kind}:${offer.endTime}` : "";

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    expiredOfferKeyRef.current = "";
  }, [activeOfferKey]);

  useEffect(() => {
    if (!offer.active || initialRemainingSeconds <= 0) {
      setCountdownEndAt(null);
      setRemainingSeconds(0);
      if (
        offer.active
        && activeOfferKey
        && expiredOfferKeyRef.current !== activeOfferKey
      ) {
        expiredOfferKeyRef.current = activeOfferKey;
        onExpireRef.current?.(offer);
      }
      return;
    }

    setCountdownEndAt(Date.now() + initialRemainingSeconds * 1000);
    setRemainingSeconds(initialRemainingSeconds);
  }, [activeOfferKey, initialRemainingSeconds, offer, offer.active, offerEndTime]);

  useEffect(() => {
    if (!countdownEndAt) {
      return;
    }

    const updateRemainingSeconds = () => {
      const nextRemainingSeconds = Math.max(
        0,
        Math.ceil((countdownEndAt - Date.now()) / 1000),
      );
      setRemainingSeconds(nextRemainingSeconds);
      if (
        nextRemainingSeconds <= 0
        && offer.active
        && activeOfferKey
        && expiredOfferKeyRef.current !== activeOfferKey
      ) {
        expiredOfferKeyRef.current = activeOfferKey;
        onExpireRef.current?.(offer);
      }
    };

    updateRemainingSeconds();
    const timer = window.setInterval(updateRemainingSeconds, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [activeOfferKey, countdownEndAt, offer]);

  return {
    remainingSeconds,
    countdown: splitCountdown(remainingSeconds),
    offerBeans: offer.active
      ? formatOfferBeans(offer.amount)
      : "0",
  };
}
